import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ExternalLink, Eye, EyeOff, Loader2, MessageSquare, Play, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { widgetApi } from '@/api/widget';
import { apiKeysApi } from '@/api/api-keys';
import { http, unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import type { WidgetConfigPatch, WidgetConfigView, WidgetTheme } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { HmacBuilderCard } from './HmacBuilderCard';
import { WidgetPreview } from './WidgetPreview';
import { WidgetAuditCard } from './WidgetAuditCard';

export function WidgetTab(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId, currentOrg } = useWorkspace();
  const callerRole = currentOrg?.role ?? null;
  const canManage = callerRole === 'owner' || callerRole === 'admin';

  const query = useQuery({
    queryKey: ['org', 'widget', currentBotId],
    queryFn: () => widgetApi.get(currentBotId!),
    enabled: !!currentBotId,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['org', 'widget', currentBotId] });
  };

  const updateMutation = useMutation({
    mutationFn: (patch: WidgetConfigPatch) => widgetApi.update(currentBotId!, patch),
    onSuccess: () => {
      toast.success(t('organization.widget.savedToast', { defaultValue: 'Widget configuration saved' }));
      invalidate();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const [rotated, setRotated] = React.useState<{ secret: string } | null>(null);
  const [testOpen, setTestOpen] = React.useState(false);
  const rotateMutation = useMutation({
    mutationFn: () => widgetApi.rotateHmac(currentBotId!),
    onSuccess: (data) => {
      setRotated({ secret: data.hmacSecret });
      invalidate();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (!currentBotId) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          {t('organization.widget.selectBot', { defaultValue: 'Select a bot to configure its widget.' })}
        </CardContent>
      </Card>
    );
  }

  if (query.isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          {t('common.loading')}
        </CardContent>
      </Card>
    );
  }

  if (query.error || !query.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">
          {unwrapError(query.error)}
        </CardContent>
      </Card>
    );
  }

  const config = query.data;

  const addOrigin = async (origin: string): Promise<void> => {
    if (config.allowedOrigins.includes(origin)) {
      toast.message('Origin already allowed');
      return;
    }
    await updateMutation.mutateAsync({ allowedOrigins: [...config.allowedOrigins, origin] });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Play className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-sm font-semibold">
              {t('organization.widget.test.ctaTitle', { defaultValue: 'Tester le widget en conditions réelles' })}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('organization.widget.test.ctaSubtitle', {
                defaultValue:
                  'Ouvre la page de test dans un nouvel onglet avec une clé widget et un token éphémère (10 min).',
              })}
            </p>
          </div>
        </div>
        <Button
          size="lg"
          className="shrink-0 gap-2 shadow-sm"
          onClick={() => setTestOpen(true)}
          disabled={!currentBotId}
        >
          <ExternalLink className="h-4 w-4" />
          {t('organization.widget.snippet.test', { defaultValue: 'Test widget' })}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-6">
        <ActivationCard
          config={config}
          canManage={canManage}
          onToggle={(enabled) => updateMutation.mutate({ enabled })}
          pending={updateMutation.isPending}
        />
        <OriginsCard
          config={config}
          canManage={canManage}
          onSave={(allowedOrigins) => updateMutation.mutate({ allowedOrigins })}
          pending={updateMutation.isPending}
        />
        <AppearanceCard
          config={config}
          canManage={canManage}
          onSave={(patch) => updateMutation.mutate(patch)}
          pending={updateMutation.isPending}
        />
        <HmacCard
          botId={currentBotId}
          config={config}
          canManage={canManage}
          onRotate={() => rotateMutation.mutate()}
          pending={rotateMutation.isPending}
        />
        <HmacBuilderCard />
        <EmbedSnippetCard config={config} onTest={() => setTestOpen(true)} />
        <WidgetAuditCard
          allowedOrigins={config.allowedOrigins}
          onAddOrigin={(o) => {
            void addOrigin(o);
          }}
        />
      </div>
      <aside className="space-y-6 lg:sticky lg:top-4 lg:self-start">
        <WidgetPreview config={config} />
      </aside>
      </div>
      {testOpen && currentBotId ? (
        <TestWidgetDialog
          botId={currentBotId}
          allowedOrigins={config.allowedOrigins}
          onAddOrigin={addOrigin}
          onClose={() => setTestOpen(false)}
        />
      ) : null}
      {rotated ? (
        <RevealSecretDialog secret={rotated.secret} onClose={() => setRotated(null)} />
      ) : null}
    </div>
  );
}

function ActivationCard({
  config,
  canManage,
  onToggle,
  pending,
}: {
  config: WidgetConfigView;
  canManage: boolean;
  onToggle: (enabled: boolean) => void;
  pending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">
            {t('organization.widget.activation.title', { defaultValue: 'Activation' })}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('organization.widget.activation.subtitle', {
              defaultValue: 'Toggle the embeddable chatbot on or off. Activation alone does not authorize any origin — configure the allowed list below.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={config.enabled ? 'success' : 'outline'} className="text-[10px]">
            {config.enabled
              ? t('organization.widget.activation.on', { defaultValue: 'Enabled' })
              : t('organization.widget.activation.off', { defaultValue: 'Disabled' })}
          </Badge>
          <Switch
            checked={config.enabled}
            disabled={!canManage || pending}
            onCheckedChange={onToggle}
          />
        </div>
      </CardHeader>
    </Card>
  );
}

function OriginsCard({
  config,
  canManage,
  onSave,
  pending,
}: {
  config: WidgetConfigView;
  canManage: boolean;
  onSave: (allowedOrigins: string[]) => void;
  pending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [entries, setEntries] = React.useState<string[]>(config.allowedOrigins);
  const [draft, setDraft] = React.useState('');

  React.useEffect(() => {
    setEntries(config.allowedOrigins);
  }, [config.allowedOrigins]);

  const dirty = JSON.stringify(entries) !== JSON.stringify(config.allowedOrigins);

  const addEntry = (): void => {
    const next = draft.trim();
    if (!next) return;
    if (entries.includes(next)) {
      toast.error(t('organization.widget.origins.duplicate', { defaultValue: 'Origin already in the list' }));
      return;
    }
    setEntries([...entries, next]);
    setDraft('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('organization.widget.origins.title', { defaultValue: 'Allowed origins' })}
        </CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('organization.widget.origins.subtitle', {
            defaultValue: 'Exact origins (https://www.acme.com) or single-label wildcards (https://*.acme.com). Calls from any other origin are rejected with HTTP 403.',
          })}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://www.acme.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addEntry();
              }
            }}
            disabled={!canManage}
          />
          <Button onClick={addEntry} disabled={!canManage || !draft.trim()} className="gap-1.5">
            <Plus className="h-4 w-4" />
            {t('common.add', { defaultValue: 'Add' })}
          </Button>
        </div>
        {entries.length === 0 ? (
          <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
            {t('organization.widget.origins.empty', {
              defaultValue: 'No origin allowed yet. The widget will reject every request until you add at least one.',
            })}
          </div>
        ) : (
          <ul className="space-y-1">
            {entries.map((entry) => (
              <li
                key={entry}
                className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-1.5 font-mono text-xs"
              >
                <span>{entry}</span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  disabled={!canManage}
                  onClick={() => setEntries(entries.filter((e) => e !== entry))}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => setEntries(config.allowedOrigins)}
            disabled={!dirty || pending}
          >
            {t('common.reset', { defaultValue: 'Reset' })}
          </Button>
          <Button
            onClick={() => onSave(entries)}
            disabled={!canManage || !dirty || pending}
            className="gap-1.5"
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AppearanceCard({
  config,
  canManage,
  onSave,
  pending,
}: {
  config: WidgetConfigView;
  canManage: boolean;
  onSave: (patch: WidgetConfigPatch) => void;
  pending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [theme, setTheme] = React.useState<WidgetTheme>(config.theme);
  const [launcherLabel, setLauncherLabel] = React.useState(config.launcherLabel);
  const [welcomeMessage, setWelcomeMessage] = React.useState(config.welcomeMessage);
  const [suggestions, setSuggestions] = React.useState<string[]>(config.suggestions);
  const [suggestionDraft, setSuggestionDraft] = React.useState('');

  React.useEffect(() => {
    setTheme(config.theme);
    setLauncherLabel(config.launcherLabel);
    setWelcomeMessage(config.welcomeMessage);
    setSuggestions(config.suggestions);
  }, [config.theme, config.launcherLabel, config.welcomeMessage, config.suggestions]);

  const dirty =
    JSON.stringify(theme) !== JSON.stringify(config.theme) ||
    launcherLabel !== config.launcherLabel ||
    welcomeMessage !== config.welcomeMessage ||
    JSON.stringify(suggestions) !== JSON.stringify(config.suggestions);

  const addSuggestion = (): void => {
    const next = suggestionDraft.trim();
    if (!next) return;
    if (suggestions.length >= 5) {
      toast.error('Maximum 5 suggestions');
      return;
    }
    if (suggestions.includes(next)) return;
    setSuggestions([...suggestions, next]);
    setSuggestionDraft('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('organization.widget.appearance.title', { defaultValue: 'Appearance' })}
        </CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('organization.widget.appearance.subtitle', {
            defaultValue: 'Visual and copy tweaks the widget reads at boot.',
          })}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="widget-color">
              {t('organization.widget.appearance.primary', { defaultValue: 'Primary color' })}
            </Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                id="widget-color"
                type="color"
                value={theme.primaryColor}
                onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded-md border bg-background"
                disabled={!canManage}
              />
              <Input
                value={theme.primaryColor}
                onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                className="font-mono"
                disabled={!canManage}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="widget-position">
              {t('organization.widget.appearance.position', { defaultValue: 'Launcher position' })}
            </Label>
            <select
              id="widget-position"
              value={theme.position}
              onChange={(e) => setTheme({ ...theme, position: e.target.value as 'br' | 'bl' })}
              className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              disabled={!canManage}
            >
              <option value="br">
                {t('organization.widget.appearance.bottomRight', { defaultValue: 'Bottom right' })}
              </option>
              <option value="bl">
                {t('organization.widget.appearance.bottomLeft', { defaultValue: 'Bottom left' })}
              </option>
            </select>
          </div>
        </div>
        <div>
          <Label htmlFor="widget-launcher-label">
            {t('organization.widget.appearance.launcherLabel', { defaultValue: 'Launcher label' })}
          </Label>
          <Input
            id="widget-launcher-label"
            value={launcherLabel}
            onChange={(e) => setLauncherLabel(e.target.value)}
            maxLength={80}
            disabled={!canManage}
          />
        </div>
        <div>
          <Label htmlFor="widget-welcome">
            {t('organization.widget.appearance.welcome', { defaultValue: 'Welcome message' })}
          </Label>
          <Textarea
            id="widget-welcome"
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            rows={3}
            maxLength={500}
            disabled={!canManage}
          />
        </div>
        <div>
          <Label htmlFor="widget-suggestion">
            {t('organization.widget.appearance.suggestions', { defaultValue: 'Quick-start suggestions' })}
          </Label>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('organization.widget.appearance.suggestionsHint', {
              defaultValue:
                'Up to 5 chips shown under the welcome message when the conversation is empty.',
            })}
          </p>
          <div className="mt-1 flex gap-2">
            <Input
              id="widget-suggestion"
              value={suggestionDraft}
              onChange={(e) => setSuggestionDraft(e.target.value)}
              maxLength={80}
              placeholder="Suivre mon colis"
              disabled={!canManage || suggestions.length >= 5}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addSuggestion();
                }
              }}
            />
            <Button
              onClick={addSuggestion}
              disabled={!canManage || !suggestionDraft.trim() || suggestions.length >= 5}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              {t('common.add', { defaultValue: 'Add' })}
            </Button>
          </div>
          {suggestions.length > 0 ? (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {suggestions.map((s) => (
                <li
                  key={s}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-muted/30 px-2.5 py-1 text-xs"
                >
                  <span>{s}</span>
                  <button
                    type="button"
                    disabled={!canManage}
                    onClick={() => setSuggestions(suggestions.filter((x) => x !== s))}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Remove suggestion"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setTheme(config.theme);
              setLauncherLabel(config.launcherLabel);
              setWelcomeMessage(config.welcomeMessage);
              setSuggestions(config.suggestions);
            }}
            disabled={!dirty || pending}
          >
            {t('common.reset', { defaultValue: 'Reset' })}
          </Button>
          <Button
            onClick={() => onSave({ theme, launcherLabel, welcomeMessage, suggestions })}
            disabled={!canManage || !dirty || pending}
            className="gap-1.5"
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function HmacCard({
  botId,
  config,
  canManage,
  onRotate,
  pending,
}: {
  botId: string;
  config: WidgetConfigView;
  canManage: boolean;
  onRotate: () => void;
  pending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [revealed, setRevealed] = React.useState<{ current: string; previous: string | null } | null>(
    null,
  );

  const revealMutation = useMutation({
    mutationFn: () => widgetApi.revealHmacSecret(botId),
    onSuccess: (data) => {
      if (!data.hmacSecret) {
        toast.error(
          t('organization.widget.hmac.revealMissing', {
            defaultValue: 'No HMAC secret on this bot yet — rotate to generate one.',
          }),
        );
        return;
      }
      setRevealed({ current: data.hmacSecret, previous: data.hmacSecretPrevious });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const showCurrent = revealed?.current ?? config.hmacSecretMasked;
  const showPrevious = revealed
    ? revealed.previous ?? null
    : config.hmacSecretPreviousMasked ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('organization.widget.hmac.title', { defaultValue: 'HMAC user signing' })}
        </CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('organization.widget.hmac.subtitle', {
            defaultValue: 'Optional. Use this secret on your backend to sign the user id before passing it to the widget. Verified anonymous sessions get a stable end-user identity tied to your account.',
          })}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>{t('organization.widget.hmac.current', { defaultValue: 'Current secret' })}</Label>
            <div className="flex items-center gap-2">
              <p className="break-all font-mono text-sm">{showCurrent}</p>
              {revealed ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  title={t('common.copy', { defaultValue: 'Copy' })}
                  onClick={() => {
                    void navigator.clipboard.writeText(revealed.current);
                    toast.success(t('common.copied', { defaultValue: 'Copied' }));
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              ) : null}
            </div>
          </div>
          <div>
            <Label>{t('organization.widget.hmac.previous', { defaultValue: 'Previous secret (grace)' })}</Label>
            <p className="break-all font-mono text-sm">
              {showPrevious ?? <span className="text-muted-foreground">—</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="gap-1.5"
            disabled={!canManage || revealMutation.isPending}
            onClick={() => {
              if (revealed) {
                setRevealed(null);
                return;
              }
              if (
                window.confirm(
                  t('organization.widget.hmac.confirmReveal', {
                    defaultValue:
                      'Reveal the HMAC secret in clear? Anyone watching this screen can read it. Only admins see this action.',
                  }),
                )
              ) {
                revealMutation.mutate();
              }
            }}
          >
            {revealMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : revealed ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
            {revealed
              ? t('organization.widget.hmac.hideButton', { defaultValue: 'Hide' })
              : t('organization.widget.hmac.revealButton', { defaultValue: 'Reveal' })}
          </Button>
          <Button
            variant="outline"
            className="gap-1.5"
            disabled={!canManage || pending}
            onClick={() => {
              if (
                window.confirm(
                  t('organization.widget.hmac.confirmRotate', {
                    defaultValue:
                      'Rotate the HMAC secret? The previous secret stays valid as a grace window until you rotate again.',
                  }),
                )
              ) {
                onRotate();
              }
            }}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {t('organization.widget.hmac.rotate', { defaultValue: 'Rotate secret' })}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmbedSnippetCard({
  config,
  onTest,
}: {
  config: WidgetConfigView;
  onTest: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { currentBotId } = useWorkspace();
  const url = config.embedSnippetUrl || 'https://zorro-widget.upela.com/v1/widget.js';
  const snippet = `<script async src="${url}"
  data-api-key="<PASTE_WIDGET_API_KEY>"
  data-user-id="optional-user-id"
  data-user-hmac="optional-hmac"></script>`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4" />
          {t('organization.widget.snippet.title', { defaultValue: 'Embed snippet' })}
        </CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('organization.widget.snippet.subtitle', {
            defaultValue:
              'Paste this on your site, replacing the API key with a widget-scoped key minted from the API keys tab.',
          })}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <pre className="overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-[11px] leading-relaxed">
          {snippet}
        </pre>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              void navigator.clipboard.writeText(snippet);
              toast.success(t('organization.widget.snippet.copied', { defaultValue: 'Snippet copied' }));
            }}
          >
            <Copy className="h-4 w-4" />
            {t('common.copy', { defaultValue: 'Copy' })}
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={onTest}
            disabled={!currentBotId}
          >
            <ExternalLink className="h-4 w-4" />
            {t('organization.widget.snippet.test', { defaultValue: 'Test widget' })}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RevealSecretDialog({
  secret,
  onClose,
}: {
  secret: string;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('organization.widget.hmac.reveal.title', { defaultValue: 'New HMAC secret' })}
          </DialogTitle>
          <DialogDescription>
            {t('organization.widget.hmac.reveal.subtitle', {
              defaultValue:
                'Copy this secret now and store it on your backend. It will never be shown again.',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border bg-muted/30 p-3">
            <code className="block break-all font-mono text-xs">{secret}</code>
          </div>
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => {
              void navigator.clipboard.writeText(secret);
              toast.success(t('organization.widget.hmac.reveal.copied', { defaultValue: 'Secret copied' }));
            }}
          >
            <Copy className="h-4 w-4" />
            {t('common.copy', { defaultValue: 'Copy' })}
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>
            {t('common.acknowledge', { defaultValue: 'I saved it' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const WIDGET_TEST_URL =
  import.meta.env.VITE_WIDGET_TEST_URL ?? 'http://localhost:5174/widget-test.html';
const WIDGET_TEST_ORIGIN = new URL(WIDGET_TEST_URL).origin;

function TestWidgetDialog({
  botId,
  allowedOrigins,
  onAddOrigin,
  onClose,
}: {
  botId: string;
  allowedOrigins: string[];
  onAddOrigin: (origin: string) => Promise<void>;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [selectedKeyHash, setSelectedKeyHash] = React.useState<string>('');

  const originAlreadyAllowed = allowedOrigins.includes(WIDGET_TEST_ORIGIN);

  const keysQuery = useQuery({
    queryKey: ['org', 'api-keys', botId],
    queryFn: () => apiKeysApi.listForBot(botId),
  });

  const widgetKeys = (keysQuery.data?.items ?? []).filter(
    (k) => k.scope === 'widget' && !k.revokedAt,
  );

  React.useEffect(() => {
    if (!selectedKeyHash && widgetKeys.length > 0) {
      setSelectedKeyHash(widgetKeys[0]!.keyHash);
    }
  }, [selectedKeyHash, widgetKeys]);

  const mintMutation = useMutation({
    mutationFn: async () => {
      const key = widgetKeys.find((k) => k.keyHash === selectedKeyHash);
      if (!key) throw new Error('Pick a key');
      const revealed = await apiKeysApi.reveal(botId, key.keyHash);
      if (!revealed.token) {
        throw new Error(
          'This key was minted before plaintext persistence — rotate it to obtain a fresh token.',
        );
      }
      const minted = await widgetApi.mintPreviewToken(botId, revealed.token);
      if (!originAlreadyAllowed) {
        await onAddOrigin(WIDGET_TEST_ORIGIN);
      }
      return minted;
    },
    onSuccess: (minted) => {
      const apiBase = http.defaults.baseURL ?? '';
      const params = new URLSearchParams({
        token: minted.token,
        api: apiBase,
      });
      window.open(`${WIDGET_TEST_URL}?${params.toString()}`, '_blank', 'noopener');
      onClose();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('organization.widget.test.title', { defaultValue: 'Test widget' })}
          </DialogTitle>
          <DialogDescription>
            {t('organization.widget.test.subtitleLocal', {
              defaultValue:
                'Opens the local test page in a new tab with a one-shot token (10 min) wrapping your widget key. The page loads the widget through the dashboard dev proxy (/_widget-dev → :5174).',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {keysQuery.isLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
            </p>
          ) : widgetKeys.length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              {t('organization.widget.test.empty', {
                defaultValue:
                  'No widget-scoped key on this bot. Create one from the API keys tab first (scope: widget).',
              })}
            </div>
          ) : (
            <div>
              <Label htmlFor="test-key">
                {t('organization.widget.test.pick', { defaultValue: 'Pick an API key' })}
              </Label>
              <select
                id="test-key"
                value={selectedKeyHash}
                onChange={(e) => setSelectedKeyHash(e.target.value)}
                className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {widgetKeys.map((k) => (
                  <option key={k.keyHash} value={k.keyHash}>
                    {k.label} — {k.keyHash.slice(0, 12)}…
                  </option>
                ))}
              </select>
            </div>
          )}
          {!originAlreadyAllowed ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
              {t('organization.widget.test.originWarning', {
                defaultValue:
                  'The test page origin {{origin}} is not in the allowed list yet. It will be added automatically before opening the test page.',
                origin: WIDGET_TEST_ORIGIN,
              })}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => mintMutation.mutate()}
            disabled={!selectedKeyHash || mintMutation.isPending}
            className="gap-1.5"
          >
            {mintMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('organization.widget.test.open', { defaultValue: 'Open test page' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
